/**
 * CEO Command Center - OpenAI Service
 * 
 * Handles all AI-powered features including:
 * - Chat assistant for natural language interactions
 * - RAG (Retrieval Augmented Generation) with knowledge base
 * - Document/transcript extraction
 * - Report generation
 * - Command parsing from AI responses
 * 
 * All API calls go through the secure Cloudflare Worker proxy.
 * 
 * @module services/api/openai
 * @version 10.2.0
 */

import { API_CONFIG, AI_CONFIG } from '../../config/constants';
import { APIError } from './client';
import type { Task, Deal, Domain, ExtractedItem } from '../../types';

// ============================================
// TYPES
// ============================================

/**
 * Chat message structure
 */
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/**
 * Chat completion options
 */
export interface ChatOptions {
  model?: string;
  maxTokens?: number;
  temperature?: number;
  stream?: boolean;
}

/**
 * AI command parsed from response
 */
export interface AICommand {
  action: 'add_task' | 'update_task' | 'add_deal' | 'update_deal' | 'mark_signal' | 'generate_report';
  data: Record<string, unknown>;
}

/**
 * Chat completion response
 */
export interface ChatResponse {
  id: string;
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// ============================================
// SYSTEM PROMPTS
// ============================================

/**
 * Default system prompt for the CEO assistant
 */
export const DEFAULT_SYSTEM_PROMPT = `You are the AI assistant for the CEO Command Center at Sentiance, a B2B mobility intelligence company.

Your role is to help the CEO (Marij) manage tasks, track deals, monitor business health, and make decisions.

You have access to:
- Tasks across 9 domains: EMG, Product, Growth, Operations, Finance, AI Automation, MAU Analytics, Fundraising, Board
- Sales pipeline (Deals) with TCV, probability, and stage tracking
- Client portfolio with health scores and contract dates
- Cash flow and runway data
- Market intelligence

When asked to perform actions, respond with JSON commands in this format:
\`\`\`json
{
  "action": "add_task",
  "data": {
    "title": "Task title",
    "domain": "EMG",
    "owner": "Owner Name",
    "priority": "High",
    "targetDate": "2026-01-15",
    "isSignal": false
  }
}
\`\`\`

Available actions:
- add_task: Create a new task
- update_task: Update existing task (include id)
- add_deal: Create a new deal
- update_deal: Update existing deal (include id)
- mark_signal: Toggle signal status on a task (include id)
- generate_report: Generate a board report

Be concise, actionable, and CEO-focused. Prioritize what matters most.
Today's date is ${new Date().toISOString().split('T')[0]}.`;

/**
 * System prompt for document extraction
 */
export const EXTRACTION_SYSTEM_PROMPT = `You are an AI assistant that extracts action items and tasks from meeting transcripts and documents.

Extract clear, actionable items with:
- A specific title describing the action
- Owner (if mentioned)
- Priority (Critical/High/Medium/Low) based on urgency language
- Target date (if mentioned, in YYYY-MM-DD format)

Return results as a JSON array:
\`\`\`json
[
  {
    "title": "Action item title",
    "owner": "Person Name",
    "priority": "High",
    "targetDate": "2026-01-15",
    "isSignal": false
  }
]
\`\`\`

Focus on clear next steps, decisions needed, and follow-up items.
Skip general discussion points that aren't actionable.`;

// ============================================
// OPENAI SERVICE
// ============================================

/**
 * Get OpenAI API URL and headers based on configuration
 */
function getOpenAIConfig() {
  if (API_CONFIG.USE_DIRECT_API && API_CONFIG.DIRECT_API.OPENAI_API_KEY) {
    return {
      url: 'https://api.openai.com/v1/chat/completions',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_CONFIG.DIRECT_API.OPENAI_API_KEY}`,
      },
    };
  }
  return {
    url: `${API_CONFIG.BASE_URL}/api/ai/chat`,
    headers: {
      'Content-Type': 'application/json',
    },
  };
}

/**
 * OpenAI service for AI-powered features
 */
export const openaiService = {
  /**
   * Send a chat completion request
   * 
   * @param messages - Array of chat messages
   * @param options - Chat options
   * @returns Chat completion response
   * 
   * @example
   * const response = await openaiService.chat([
   *   { role: 'user', content: 'What are my top priorities today?' }
   * ]);
   */
  async chat(
    messages: ChatMessage[],
    options: ChatOptions = {}
  ): Promise<ChatResponse> {
    const {
      model = AI_CONFIG.DEFAULT_MODEL,
      maxTokens = AI_CONFIG.MAX_TOKENS.CHAT,
      temperature = AI_CONFIG.TEMPERATURE.CHAT,
    } = options;
    
    const config = getOpenAIConfig();
    
    const response = await fetch(config.url, {
      method: 'POST',
      headers: config.headers,
      body: JSON.stringify({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new APIError(
        error.error?.message || error.error || `AI request failed: ${response.status}`,
        response.status,
        error
      );
    }
    
    return response.json();
  },
  
  /**
   * Send a streaming chat completion request
   * Streams tokens as they're generated
   * Note: Streaming may not work in direct API mode due to CORS
   * 
   * @param messages - Array of chat messages
   * @param options - Chat options
   * @param onChunk - Callback for each chunk of text
   * @returns Complete response text
   * 
   * @example
   * const text = await openaiService.chatStream(
   *   messages,
   *   {},
   *   (chunk) => console.log(chunk)
   * );
   */
  async chatStream(
    messages: ChatMessage[],
    options: ChatOptions = {},
    onChunk: (chunk: string) => void = () => {}
  ): Promise<string> {
    const {
      model = AI_CONFIG.DEFAULT_MODEL,
      maxTokens = AI_CONFIG.MAX_TOKENS.CHAT,
      temperature = AI_CONFIG.TEMPERATURE.CHAT,
    } = options;
    
    // For direct API mode, use non-streaming endpoint
    if (API_CONFIG.USE_DIRECT_API && API_CONFIG.DIRECT_API.OPENAI_API_KEY) {
      const response = await this.chat(messages, options);
      const content = response.choices[0]?.message?.content || '';
      onChunk(content);
      return content;
    }
    
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/ai/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
        stream: true,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new APIError(
        error.error || 'Streaming failed',
        response.status,
        error
      );
    }
    
    const reader = response.body?.getReader();
    if (!reader) {
      throw new APIError('No response body', 500);
    }
    
    const decoder = new TextDecoder();
    let fullText = '';
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n').filter(line => line.startsWith('data:'));
      
      for (const line of lines) {
        const data = line.slice(5).trim();
        if (data === '[DONE]') continue;
        
        try {
          const parsed = JSON.parse(data);
          const content = parsed.choices?.[0]?.delta?.content || '';
          if (content) {
            fullText += content;
            onChunk(content);
          }
        } catch {
          // Skip invalid JSON lines
        }
      }
    }
    
    return fullText;
  },
  
  /**
   * Parse AI commands from response text
   * Extracts JSON command blocks from AI responses
   * 
   * @param text - AI response text
   * @returns Array of parsed commands
   * 
   * @example
   * const commands = openaiService.parseCommands(responseText);
   * for (const cmd of commands) {
   *   if (cmd.action === 'add_task') {
   *     await createTask(cmd.data);
   *   }
   * }
   */
  parseCommands(text: string): AICommand[] {
    const commands: AICommand[] = [];
    
    // Match JSON blocks in markdown code blocks
    const jsonPattern = /```(?:json)?\s*([\s\S]*?)```/g;
    let match;
    
    while ((match = jsonPattern.exec(text)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        
        // Handle single command object
        if (parsed.action && parsed.data) {
          commands.push(parsed as AICommand);
        }
        // Handle array of commands
        else if (Array.isArray(parsed)) {
          for (const item of parsed) {
            if (item.action && item.data) {
              commands.push(item as AICommand);
            }
          }
        }
      } catch {
        // Skip invalid JSON
        console.warn('Failed to parse AI command JSON');
      }
    }
    
    // Also try to find inline JSON (not in code blocks)
    const inlinePattern = /\{[\s\S]*?"action"[\s\S]*?"data"[\s\S]*?\}/g;
    while ((match = inlinePattern.exec(text)) !== null) {
      try {
        const parsed = JSON.parse(match[0]);
        if (parsed.action && parsed.data) {
          // Avoid duplicates
          const isDuplicate = commands.some(
            c => c.action === parsed.action && 
                 JSON.stringify(c.data) === JSON.stringify(parsed.data)
          );
          if (!isDuplicate) {
            commands.push(parsed as AICommand);
          }
        }
      } catch {
        // Skip invalid JSON
      }
    }
    
    return commands;
  },
  
  /**
   * Extract action items from a document or transcript
   * 
   * @param text - Document text to analyze
   * @param domain - Domain for extracted tasks
   * @returns Array of extracted items
   * 
   * @example
   * const items = await openaiService.extractFromDocument(
   *   transcriptText,
   *   'EMG'
   * );
   */
  async extractFromDocument(
    text: string,
    domain: Domain
  ): Promise<ExtractedItem[]> {
    const response = await this.chat(
      [
        { role: 'system', content: EXTRACTION_SYSTEM_PROMPT },
        { role: 'user', content: `Extract action items from this ${domain} document:\n\n${text}` },
      ],
      {
        temperature: AI_CONFIG.TEMPERATURE.EXTRACTION,
        maxTokens: AI_CONFIG.MAX_TOKENS.EXTRACTION,
      }
    );
    
    const content = response.choices[0]?.message?.content || '';
    
    // Parse extracted items
    const items: ExtractedItem[] = [];
    const jsonPattern = /```(?:json)?\s*([\s\S]*?)```/g;
    let match;
    
    while ((match = jsonPattern.exec(content)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        const array = Array.isArray(parsed) ? parsed : [parsed];
        
        for (const item of array) {
          items.push({
            id: `extracted-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            title: item.title || '',
            owner: item.owner || '',
            priority: item.priority || 'Medium',
            targetDate: item.targetDate || null,
            domain,
            selected: true,
            status: 'Not Started',
            isSignal: item.isSignal || false,
          });
        }
      } catch {
        console.warn('Failed to parse extracted items');
      }
    }
    
    return items;
  },
  
  /**
   * Generate a board report
   * 
   * @param context - Context data for the report
   * @returns Generated report text
   */
  async generateBoardReport(context: {
    tasks: Task[];
    deals: Deal[];
    signals: Array<{ title: string; level: string }>;
    boardReadiness: { score: number; issues: string[] };
  }): Promise<string> {
    const prompt = `Generate a concise board update report based on this data:

Signals (${context.signals.length}):
${context.signals.map(s => `- [${s.level.toUpperCase()}] ${s.title}`).join('\n')}

Board Readiness: ${context.boardReadiness.score}%
Issues: ${context.boardReadiness.issues.join(', ') || 'None'}

Active Tasks: ${context.tasks.filter(t => t.status !== 'Done').length}
In Progress: ${context.tasks.filter(t => t.status === 'In Progress').length}

Pipeline: ${context.deals.filter(d => !['Closed Won', 'Closed Lost'].includes(d.stage)).length} active deals

Format the report with:
1. Executive Summary (2-3 sentences)
2. Key Signals Requiring Attention
3. Pipeline Overview
4. Recommended Actions`;

    const response = await this.chat(
      [
        { role: 'system', content: 'You are a professional business analyst preparing CEO board reports. Be concise and action-oriented.' },
        { role: 'user', content: prompt },
      ],
      {
        temperature: AI_CONFIG.TEMPERATURE.REPORT,
        maxTokens: AI_CONFIG.MAX_TOKENS.REPORT,
      }
    );
    
    return response.choices[0]?.message?.content || '';
  },
  
  /**
   * Build context string for chat
   * Summarizes current data for AI context
   * 
   * @param data - Current application data
   * @returns Context string for system prompt
   */
  buildContext(data: {
    tasks: Task[];
    deals: Deal[];
    signals?: Array<{ title: string; level: string }>;
  }): string {
    const activeTasks = data.tasks.filter(t => t.status !== 'Done');
    const today = new Date().toISOString().split('T')[0];
    const overdueTasks = data.tasks.filter(t => t.targetDate && t.targetDate < today && t.status !== 'Done');
    const activeDeals = data.deals.filter(d => !['Closed Won', 'Closed Lost'].includes(d.stage));
    
    const context = `
CURRENT STATE:
- Active Tasks: ${activeTasks.length} (${overdueTasks.length} overdue)
- Active Deals: ${activeDeals.length}
- Signals: ${data.signals?.length || 0}

TOP OVERDUE TASKS:
${overdueTasks.slice(0, 3).map(t => `- ${t.title} (${t.owner || 'Unassigned'})`).join('\n') || 'None'}

TOP DEALS:
${activeDeals
  .sort((a, b) => (b.tcv || 0) - (a.tcv || 0))
  .slice(0, 3)
  .map(d => `- ${d.prospect}: €${((d.tcv || 0) / 1000).toFixed(0)}k (${d.stage})`)
  .join('\n')}
`;
    
    return context;
  },
  
  /**
   * Estimate token count for a string
   * Rough approximation: ~4 characters per token
   * 
   * @param text - Text to estimate
   * @returns Estimated token count
   */
  estimateTokens(text: string): number {
    return Math.ceil(text.length / 4);
  },
  
  /**
   * RAG-enhanced chat completion
   * Automatically retrieves relevant knowledge and augments the prompt
   * 
   * @param userMessage - User's message
   * @param history - Previous chat history
   * @param options - Chat options
   * @returns Response with content and sources
   */
  async chatWithRAG(
    userMessage: string,
    history: ChatMessage[] = [],
    options: ChatOptions = {}
  ): Promise<{ content: string; sources: Array<{ id: string; title: string; category: string; relevance: number }> }> {
    // Dynamic import to avoid circular dependencies
    const { ragService } = await import('../rag');
    
    // Augment prompt with knowledge
    const ragResult = ragService.augmentPrompt(userMessage);
    
    // Build messages with RAG system prompt enhancement
    const systemPrompt = ragResult.sources.length > 0
      ? `${DEFAULT_SYSTEM_PROMPT}\n\n${ragService.getRAGSystemPrompt()}`
      : DEFAULT_SYSTEM_PROMPT;
    
    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      ...history,
      { role: 'user', content: ragResult.augmentedPrompt },
    ];
    
    // Get response
    const response = await this.chat(messages, options);
    const content = response.choices[0]?.message?.content || '';
    
    return {
      content,
      sources: ragResult.sources,
    };
  },
  
  /**
   * RAG-enhanced streaming chat
   * 
   * @param userMessage - User's message
   * @param history - Previous chat history
   * @param options - Chat options
   * @param onChunk - Callback for each chunk
   * @returns Response with content and sources
   */
  async chatStreamWithRAG(
    userMessage: string,
    history: ChatMessage[] = [],
    options: ChatOptions = {},
    onChunk: (chunk: string) => void = () => {}
  ): Promise<{ content: string; sources: Array<{ id: string; title: string; category: string; relevance: number }> }> {
    // Dynamic import to avoid circular dependencies
    const { ragService } = await import('../rag');

    // Augment prompt with knowledge
    const ragResult = ragService.augmentPrompt(userMessage);

    // Build messages with RAG system prompt enhancement
    const systemPrompt = ragResult.sources.length > 0
      ? `${DEFAULT_SYSTEM_PROMPT}\n\n${ragService.getRAGSystemPrompt()}`
      : DEFAULT_SYSTEM_PROMPT;

    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      ...history,
      { role: 'user', content: ragResult.augmentedPrompt },
    ];

    // Get streaming response
    const content = await this.chatStream(messages, options, onChunk);

    return {
      content,
      sources: ragResult.sources,
    };
  },

  /**
   * Chat with AI using function calling for tool use
   *
   * @param messages - Array of chat messages
   * @param tools - Array of tools in OpenAI format
   * @param options - Chat options
   * @returns Chat response with potential tool calls
   */
  async chatWithTools(
    messages: ChatMessage[],
    tools: Array<{
      type: 'function';
      function: {
        name: string;
        description: string;
        parameters: Record<string, unknown>;
      };
    }>,
    options: ChatOptions = {}
  ): Promise<{
    content: string | null;
    toolCalls: Array<{
      id: string;
      type: 'function';
      function: {
        name: string;
        arguments: string;
      };
    }> | null;
    finishReason: string;
  }> {
    const {
      model = AI_CONFIG.DEFAULT_MODEL,
      maxTokens = AI_CONFIG.MAX_TOKENS.CHAT,
      temperature = AI_CONFIG.TEMPERATURE.CHAT,
    } = options;

    const config = getOpenAIConfig();

    const response = await fetch(config.url, {
      method: 'POST',
      headers: config.headers as HeadersInit,
      body: JSON.stringify({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
        tools,
        tool_choice: 'auto',
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new APIError(
        error.error?.message || error.error || `AI request failed: ${response.status}`,
        response.status,
        error
      );
    }

    const data = await response.json();
    const choice = data.choices?.[0];

    return {
      content: choice?.message?.content || null,
      toolCalls: choice?.message?.tool_calls || null,
      finishReason: choice?.finish_reason || 'stop',
    };
  },

  /**
   * Continue conversation after tool execution
   *
   * @param messages - Original messages plus tool results
   * @param options - Chat options
   * @returns Final chat response
   */
  async continueAfterToolCall(
    messages: Array<ChatMessage | { role: 'tool'; tool_call_id: string; content: string }>,
    options: ChatOptions = {}
  ): Promise<string> {
    const {
      model = AI_CONFIG.DEFAULT_MODEL,
      maxTokens = AI_CONFIG.MAX_TOKENS.CHAT,
      temperature = AI_CONFIG.TEMPERATURE.CHAT,
    } = options;

    const config = getOpenAIConfig();

    const response = await fetch(config.url, {
      method: 'POST',
      headers: config.headers as HeadersInit,
      body: JSON.stringify({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new APIError(
        error.error?.message || error.error || `AI request failed: ${response.status}`,
        response.status,
        error
      );
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || '';
  },
};

// ============================================
// EXPORT
// ============================================

export default openaiService;
